import React, { useState, useEffect, useMemo, useCallback } from 'react';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import { Grid, makeStyles } from '@material-ui/core';

function itemNameMatchesQuery(name, queryLower) {
  if (!queryLower) return true;
  return String(name ?? '')
    .toLowerCase()
    .includes(queryLower);
}

export const useStyles = makeStyles(() => ({
  textfield: {
    width: ({ width }) => width || '105px',
    border: '1px solid rgba(228, 231, 236, 1)',
    borderRadius: '8px !important',
    background: 'rgba(254, 254, 254, 1)',
    height: ({ height }) => height || '33px',
    padding: '0px 8px',
  },
  formControl: {
    width: ({ width }) => width || '105px',
    height: ({ height }) => height || '33px',
    margin: '0px !important',
    border: 'none',
  },
  count: {
    width: '15px',
    height: '15px',
    borderRadius: '50%',
    position: 'absolute',
    top: '50%',
    left: '7px',
    transform: 'translateY(-50%)',
    background: '#D9D6FE',
    fontWeight: '500',
    fontSize: '8px',
    color: '#622FEE',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '99',
  },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const CustomMultiSelect = ({
  width,
  height,
  list,
  handleFilterChange,
  name,
  selectedValue,
  placeholder = '',
  showSelectedValues = true,
  enableSearch = false,
}) => {
  const classes = useStyles({ width, height });
  const [showCount, setShowCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setShowCount(selectedValue?.length);
  }, [selectedValue]);

  const queryLower = useMemo(() => searchQuery.trim().toLowerCase(), [searchQuery]);

  const filteredList = useMemo(() => {
    if (!enableSearch || !queryLower) return list ?? [];
    return (list ?? []).filter((item) => itemNameMatchesQuery(item.name, queryLower));
  }, [list, queryLower, enableSearch]);

  const handleSelectClose = useCallback(() => {
    if (enableSearch) setSearchQuery('');
  }, [enableSearch]);

  const handleSearchChange = useCallback((event) => {
    const next = event.target.value;
    setSearchQuery(next);
  }, []);

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;

    if (value.includes(0)) {
      handleFilterChange(selectedValue.length === list.length ? [] : list.map((item) => item.id), name);
    } else {
      handleFilterChange(typeof value === 'string' ? value.split(',') : value, name);
    }
  };

  const selectedNames = useMemo(
    () =>
      selectedValue
        ?.map((id) => {
          const item = list?.find((i) => i.id === id);
          return item ? item.name : null;
        })
        .filter(Boolean)
        .join(', '),
    [selectedValue, list],
  );

  const renderSelectedValues = () => {
    if (!selectedValue || selectedValue.length === 0) {
      return <span style={{ color: '#aaa' }}>{placeholder}</span>;
    }
    return showSelectedValues ? selectedNames : placeholder;
  };

  return (
    <div>
      <FormControl sx={{ m: 1 }} size="small" className={classes.formControl}>
        {showCount > 0 && (
          <Grid className={classes.count}>
            {selectedValue?.length === list?.length ? 'All' : selectedValue?.length}
          </Grid>
        )}
        <Select
          labelId="demo-multiple-checkbox-label"
          id="demo-multiple-checkbox"
          name={name}
          multiple
          value={Array.isArray(selectedValue) ? selectedValue : []}
          onChange={handleChange}
          input={<OutlinedInput className={classes.textfield} />}
          displayEmpty
          renderValue={renderSelectedValues}
          onClose={handleSelectClose}
          MenuProps={{
            ...MenuProps,
            autoFocus: !enableSearch,
          }}
          sx={{
            fontFamily: 'Lato',
            fontWeight: '500',
            fontSize: '12px',
            color: '#1D2939',
            boxShadow: 'none',
            '.MuiOutlinedInput-notchedOutline': { border: 0 },
            '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              border: 0,
            },
            '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 0,
            },
          }}
        >
          {enableSearch && (
            <ListSubheader sx={{ lineHeight: 'normal', py: 1, px: 1.5 }} onKeyDown={(e) => e.stopPropagation()}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search"
                value={searchQuery}
                onChange={handleSearchChange}
                inputProps={{ 'aria-label': 'Filter options' }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                sx={{
                  '& .MuiOutlinedInput-root': { fontFamily: 'Lato', fontSize: '12px' },
                }}
              />
            </ListSubheader>
          )}
          <MenuItem value={0} disableRipple disableTouchRipple>
            <Checkbox checked={selectedValue?.length === list?.length} size="small" disableRipple disableTouchRipple />
            <ListItemText
              primary="All"
              primaryTypographyProps={{ fontFamily: 'Lato', fontWeight: '500', fontSize: '12px', lineHeight: '16px' }}
            />
          </MenuItem>
          {enableSearch && filteredList.length === 0 ? (
            <MenuItem disabled dense>
              <ListItemText
                primary="No matches"
                primaryTypographyProps={{ fontFamily: 'Lato', fontWeight: '500', fontSize: '12px', lineHeight: '16px' }}
              />
            </MenuItem>
          ) : (
            (enableSearch ? filteredList : list)?.map((item) => (
              <MenuItem key={item.id} value={item.id} disableRipple disableTouchRipple>
                <Checkbox checked={selectedValue?.includes(item.id)} size="small" disableRipple disableTouchRipple />
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{
                    fontFamily: 'Lato',
                    fontWeight: '500',
                    fontSize: '12px',
                    lineHeight: '16px',
                  }}
                />
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    </div>
  );
};

export default React.memo(CustomMultiSelect);
